/**
 * Returns a safe same-origin path for post-auth redirect, or null if unsafe / invalid.
 */
export function parseSafeInternalNextPath(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "" || !s.startsWith("/")) return null;
  if (s.startsWith("//")) return null;
  if (s.includes("://")) return null;
  if (s.includes("\\")) return null;
  if (s.length > 2048) return null;
  const pathOnly = s.split("?")[0].split("#")[0].toLowerCase();
  if (
    pathOnly.startsWith("/signin") ||
    pathOnly.startsWith("/signup") ||
    pathOnly.startsWith("/verify-email") ||
    pathOnly.startsWith("/verify-mobile") ||
    pathOnly.startsWith("/nd1/signin") ||
    pathOnly.startsWith("/nd1/signup")
  ) {
    return null;
  }
  return s;
}

/** `/signin`, `/nd1/signin`, `/signup`, or `/nd1/signup` with optional `?next=`. */
export function buildAuthHrefWithNext(basePath, nextRaw) {
  const base = String(basePath || "/signin").trim() || "/signin";
  const path = parseSafeInternalNextPath(nextRaw);
  if (!path) return base;
  return `${base}?next=${encodeURIComponent(path)}`;
}

export function needsEmailVerification(user) {
  return user?.emailVerified === false;
}

export function needsPhoneVerification(user) {
  return user?.phoneVerified === false;
}

export function isUserFullyVerified(user) {
  if (!user) return false;
  return !needsEmailVerification(user) && !needsPhoneVerification(user);
}

/** Path to verify email/mobile or the signed-in workspace (with optional ?next=). */
export function resolvePostAuthPath(user, nextPath) {
  const safeNext = parseSafeInternalNextPath(nextPath);

  if (needsEmailVerification(user)) {
    const qs = new URLSearchParams();
    const email = String(user?.email || "").trim();
    if (email) qs.set("email", email);
    if (safeNext) qs.set("next", safeNext);
    const q = qs.toString();
    return q ? `/verify-email?${q}` : "/verify-email";
  }

  if (needsPhoneVerification(user)) {
    return safeNext
      ? `/verify-mobile?next=${encodeURIComponent(safeNext)}`
      : "/verify-mobile";
  }

  if (safeNext) return safeNext;
  if (user?.role === "hiring-manager" || user?.role === "recruiter") {
    return "/hiring-dashboard";
  }
  return "/applicant-home";
}

/** Route after successful sign-in / sign-up (mock + API user shape). */
export function navigateAfterSignIn(navigate, user, options) {
  const rawNext = options && typeof options.nextPath === "string" ? options.nextPath : null;
  navigate(resolvePostAuthPath(user, rawNext));
}

const PROTECTED_PATH_PREFIXES = [
  "/applicant-home",
  "/applicant-profile",
  "/applicant/",
  "/applicant-jobs/",
  "/job-search",
  "/applicant-search/",
  "/hiring-dashboard",
  "/hiring/",
  "/recruitment-profile",
  "/response-settings",
  "/purchases",
  "/notifications",
];

const VERIFY_FLOW_PREFIXES = ["/verify-email", "/verify-mobile"];

/** Sign-in, sign-up, verification, and password-reset flows (incl. old1 / nd1). */
const AUTH_JOURNEY_PREFIXES = [
  "/signin",
  "/signup",
  "/verify-email",
  "/verify-mobile",
  "/forgot-password",
  "/reset-password",
  "/nd1/signin",
  "/nd1/signup",
  "/old1/signin",
  "/old1/signup",
  "/old1/verify-email",
  "/old1/verify-mobile",
  "/old1/forgot-password",
  "/old1/reset-password",
  "/change-email",
  "/change-mobile",
  "/old1/change-email",
  "/old1/change-mobile",
];

export function isAuthJourneyPath(pathname) {
  const path = String(pathname || "")
    .split("?")[0]
    .split("#")[0]
    .toLowerCase();
  return AUTH_JOURNEY_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

export function isProtectedProductPath(pathname) {
  const path = String(pathname || "");
  return PROTECTED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(prefix)
  );
}

export function isVerifyFlowPath(pathname) {
  const path = String(pathname || "");
  return VERIFY_FLOW_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(prefix)
  );
}

/** Marketing, legal, guest job detail, etc. — tawk.to loads here only (not in-session product routes). */
const TAWK_PUBLIC_PATH_OVERRIDES = ["/job-search"];

export function isPublicTawkPath(pathname) {
  const path = String(pathname || "")
    .split("?")[0]
    .split("#")[0];
  if (
    TAWK_PUBLIC_PATH_OVERRIDES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    )
  ) {
    return true;
  }
  if (isAuthJourneyPath(pathname)) return false;
  if (isProtectedProductPath(pathname)) return false;
  return true;
}

export function resolveVerificationRedirect(currentUser, pathname, search = "") {
  if (!currentUser || isUserFullyVerified(currentUser)) return null;
  if (isVerifyFlowPath(pathname)) return null;
  if (!isProtectedProductPath(pathname)) return null;

  const nextPath = `${pathname}${search || ""}`;
  const target = resolvePostAuthPath(currentUser, nextPath);
  const current = `${pathname}${search || ""}`;
  if (target === current) return null;
  return target;
}
